import { MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Theme, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { CheckCircleIcon } from 'assets';
import {
  SUCCESS_OPERATION_ACTION_TYPE_CARD_CHANGE,
  SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE,
} from 'constants/payment';
import { SECOND_ACCOUNT_PAYMENT_REQUIRED } from 'constants/index';
import { SuccessInfoDialog } from 'components';
import { useApiRequests, useDialog, useTrackingAnalytics } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store';
import { getUser, selectUser } from 'store/user';
import { isHipaaLink } from 'config/productProfile';

import styles from './SuccessPaymentPage.module.scss';
import { ApiUser, TrackingEventsEnum } from 'types';

type ActionData = {
  title: string;
  buttonText: string;
  bodyText: string;
};

export function SuccessPaymentPage() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { showDialog } = useDialog();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const trackingAnalyticsContext = useTrackingAnalytics();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('settings');
  const { postCheckoutSubscriptionUpdate } = useApiRequests();
  const user: ApiUser | null = useAppSelector(selectUser);

  const postChackputStarted: MutableRefObject<boolean> = useRef<boolean>(false);

  const { search } = useLocation();
  const type: string | null = new URLSearchParams(search).get('type');
  const redirectTo: string | null = new URLSearchParams(search).get('redirectTo');
  const redirectToHash: string | null = new URLSearchParams(search).get('redirectToHash');
  const hostedpageId: string | null = new URLSearchParams(search).get('hostedpage_id');
  const navigateTo = `/${redirectTo ?? ''}${redirectToHash ? '#' + redirectToHash! : ''}`;

  const actionData: ActionData = useMemo(() => {
    switch (type) {
      case SUCCESS_OPERATION_ACTION_TYPE_CARD_CHANGE:
        return {
          title: t('successPayment.cardChangeTitle'),
          bodyText: t('successPayment.cardChangeBody'),
          buttonText: t('successPayment.ok'),
        };
      case SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE:
      default:
        return {
          title: t('successPayment.subscriptionChangeTitle'),
          bodyText: t('successPayment.subscriptionChangeBody'),
          buttonText: t('successPayment.getStarted'),
        };
    }
  }, [t, type]);

  useEffect(() => {
    if (!user || postChackputStarted.current) {
      return;
    }

    postChackputStarted.current = true;

    const postCheckoutCallback = (): Promise<void> => {
      if (!hostedpageId) {
        return Promise.resolve();
      }

      return postCheckoutSubscriptionUpdate(hostedpageId ?? '').then((data) => {
        const amount = data.subscription?.amount ?? 0;
        // const intervalUnit = data.subscription?.intervalUnit === 'Months' ? 12 : 1;

        trackingAnalyticsContext.trackEvent(TrackingEventsEnum.CompleteCheckout, {
          email: user.email,
          value: amount,
          currency: 'USD',
          // predicted_ltv: amount * intervalUnit,
        });
      });
    };

    postCheckoutCallback()
      .then(() => {
        return dispatch(getUser()).unwrap();
      })
      .then(() => {
        if (type === SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE) {
          localStorage.removeItem(SECOND_ACCOUNT_PAYMENT_REQUIRED);
        }

        if (isMobile) {
          return;
        }

        const modal = showDialog(SuccessInfoDialog, {
          buttonText: actionData.buttonText,
          title: actionData.title,
          body: (
            <>
              <Typography component='p' variant='body5'>
                {actionData.bodyText}
              </Typography>
              <Typography component='p' variant='body5'>
                {t('successPayment.thankYouForChoosing', { brand: isHipaaLink ? 'HIPAA LINK' : 'VIA3 UNITY' })}
              </Typography>
            </>
          ),
          onConfirm: () => {
            modal.destroy();
          },
        });
        navigate(navigateTo);
      })
      .catch((error) => {
        enqueueSnackbar(
          t('subscriptionNotifications.postCheckoutSubscriptionUpdateError', { message: error.message }),
          {
            variant: 'error',
          },
        );
      });
  }, [
    actionData.bodyText,
    actionData.buttonText,
    actionData.title,
    dispatch,
    enqueueSnackbar,
    hostedpageId,
    isMobile,
    navigate,
    navigateTo,
    postCheckoutSubscriptionUpdate,
    showDialog,
    t,
    trackingAnalyticsContext,
    type,
    user,
  ]);

  const handleGetStarted = (): void => {
    if (type === SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE) {
      localStorage.removeItem(SECOND_ACCOUNT_PAYMENT_REQUIRED);
    }
    navigate(navigateTo);
  };

  if (!isMobile) return null;

  return (
    <Box className={styles.container}>
      <CheckCircleIcon className={styles.icon} color='primary' />

      <Typography component='p' variant='body4'>
        {actionData.title}
      </Typography>

      <Typography component='p' variant='body2'>
        {actionData.bodyText}
      </Typography>

      <Typography component='p' variant='body2'>
        {t('successPayment.thankYouForChoosing', { brand: isHipaaLink ? 'HIPAA LINK' : 'VIA3 UNITY' })}
      </Typography>

      <Button variant='contained' onClick={handleGetStarted} className={styles.formButton}>
        {actionData.buttonText}
      </Button>
    </Box>
  );
}
